import React from 'react';
import { type AlertThresholdsSettings } from '../../api/types.js';
import { InputField } from './Field.js';
import ToggleRow from './ToggleRow.js';
import SaveBar from './SaveBar.js';
import { useEditable } from './useEditable.js';
import { PerimeterConfig } from '../../api/types.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface Props {
  value: AlertThresholdsSettings;
  onSave: (next: AlertThresholdsSettings) => Promise<void>;
}

interface RangeRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
}

function RangeRow({ label, value, min, max, onChange }: RangeRowProps) {
  return (
    <div className="range-row">
      <div className="range-top">
        <span className="range-label">{label}</span>
        <span className="range-val">{value}m</span>
      </div>
      <input
        className="range-input"
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function AlertThresholdsTab({ value, onSave }: Props) {
  const { t } = useTranslation();
  const { draft, setNested, dirty, saving, reset, save } = useEditable(value, onSave);
  const p = draft.perimeters;
  const s = draft.scoring;

  return (
    <div className="sp on">
      <div className="sp-title">{t('alertThresholds.title')}</div>
      <div className="sp-subtitle">{t('alertThresholds.subtitle')}</div>

      <div className="sg">
        <div className="sg-title">{t('alertThresholds.proximityPerimeters')}</div>

        <div className="thresh-card crit">
          <div className="thresh-name" style={{ color: 'var(--red)' }}>
            {t('alertThresholds.criticalZone')}
          </div>
          <RangeRow
            label={t('alertThresholds.distance')}
            value={p.critical.distanceM}
            min={20}
            max={p.alert.distanceM < 100 ? p.alert.distanceM : 100}
            onChange={(v) => setNested('perimeters', { critical: { ...p.critical, distanceM: v } })}
          />
          <ToggleRow
            label={t('alertThresholds.triggerOverlay')}
            checked={p.critical.triggerOverlay}
            onChange={(v) => setNested('perimeters', { critical: { ...p.critical, triggerOverlay: v } })}
          />
          <ToggleRow
            label={t('alertThresholds.autoPan')}
            checked={p.critical.autoPan}
            onChange={(v) => setNested('perimeters', { critical: { ...p.critical, autoPan: v } })}
          />
        </div>

        <div className="thresh-card crit">
          <div className="thresh-name" style={{ color: 'var(--red)' }}>
            {t('alertThresholds.alertZone')}
          </div>
          <RangeRow
            label={t('alertThresholds.distance')}
            value={p.alert.distanceM}
            min={p.critical.distanceM > 50 ? p.critical.distanceM : 50}
            max={p.caution.distanceM < 200 ? p.caution.distanceM : 200}
            onChange={(v) => setNested('perimeters', { alert: { distanceM: v } })}
          />
        </div>

        <div className="thresh-card warn">
          <div className="thresh-name" style={{ color: 'var(--ora)' }}>
            {t('alertThresholds.cautionZone')}
          </div>
          <RangeRow
            label={t('alertThresholds.distance')}
            value={p.caution.distanceM}
            min={p.alert.distanceM > 100 ? p.alert.distanceM : 100}
            max={p.watch.distanceM < 250 ? p.watch.distanceM : 250}
            onChange={(v) => setNested('perimeters', { caution: { distanceM: v } })}
          />
        </div>

        <div className="thresh-card caut">
          <div className="thresh-name" style={{ color: 'var(--gold)' }}>
            {t('alertThresholds.watchZone')}
          </div>
          <RangeRow
            label={t('alertThresholds.distance')}
            value={p.watch.distanceM}
            min={p.caution.distanceM > 150 ? p.caution.distanceM : 150}
            max={500}
            onChange={(v) => setNested('perimeters', { watch: { distanceM: v } })}
          />
        </div>
      </div>

      <div className="sg">
        <div className="sg-title">{t('alertThresholds.scoringThresholds')}</div>
        <div className="sg-grid">
          <InputField
            label={t('alertThresholds.autoFlag')}
            type="number"
            min={0}
            max={100}
            value={s.autoFlag}
            hint={t('alertThresholds.autoFlagHint')}
            onChange={(v) => setNested('scoring', { autoFlag: Number(v) })}
            readOnly // TODO:
          />
          <InputField
            label={t('alertThresholds.mediumThreat')}
            type="number"
            min={0}
            max={100}
            value={s.medium}
            onChange={(v) => setNested('scoring', { medium: Number(v) })}
            readOnly // TODO:
          />
          <InputField
            label={t('alertThresholds.highThreat')}
            type="number"
            min={0}
            max={100}
            value={s.high}
            onChange={(v) => setNested('scoring', { high: Number(v) })}
            readOnly // TODO:
          />
          <InputField
            label={t('alertThresholds.criticalThreshold')}
            type="number"
            min={0}
            max={100}
            value={s.critical}
            onChange={(v) => setNested('scoring', { critical: Number(v) })}
            readOnly // TODO:
          />
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onCancel={reset} onSave={save} />
    </div>
  );
}

export default AlertThresholdsTab;
